import config from 'config';

const {
  constants: {
    API: { PRICING_UPDATE_URL },
  },
} = config;

/**
 * `agentCause` rather than Error's own `cause`: the `new Error(msg, { cause })`
 * options bag is silently ignored on browsers that predate it, which would
 * leave every error looking like an unnamed failure. The value is whatever
 * string the backend put in `error.cause` — this core stays agnostic about the
 * vocabulary, each agent client maps it to its own cause enum.
 */
export function agentError(message, cause) {
  const err = new Error(message);
  err.agentCause = cause;
  return err;
}

/**
 * Generic streaming agent-chat client. Reads SSE events from a backend agent
 * endpoint and invokes callbacks as each event arrives. Shared by
 * PropertyAIChat and SearchAIChat — the only per-agent differences are the
 * endpoint path and the tool set, both passed in by the caller.
 *
 * Backend SSE shape: the WildFly SseEmitter wraps each forwarded line in a
 * "data:" prefix; the inner payload is a raw Anthropic SSE line which itself
 * has "event:"/"data:" prefixes. We strip both layers before parsing JSON.
 *
 * Multi-tool parsing: Anthropic streams every content block with an `index`.
 * We key in-flight tool_use blocks by that index, so a single turn containing
 * multiple tool_use blocks is parsed correctly instead of a later block
 * clobbering an earlier one (the previous single-variable implementation).
 *
 * Abort: pass an AbortController `signal` to cancel an in-flight stream
 * (component unmount or a user "Stop" action). Aborts resolve silently — no
 * onError, no onDone.
 *
 * Terminal events: `onDone` fires exactly once, whether it was reached via a
 * `message_stop` event or by the stream simply closing. A stream that closes
 * with neither a `message_stop` nor an `error` event did not finish, and
 * `onIncomplete` is the caller's hook for that case; callers that omit it just
 * see the ordinary `onDone`.
 *
 * @param {object}   opts
 * @param {string}   opts.endpoint   Path appended to PRICING_UPDATE_URL.
 * @param {Array}    opts.history    [{ role, content }] message history.
 * @param {object}   opts.context    Agent-specific context payload.
 * @param {string}   opts.sessionId  Stable per-conversation id.
 * @param {Array}    opts.tools      Tool definitions sent to the backend.
 * @param {AbortSignal} [opts.signal]
 * @param {function} [opts.onText]    (text) => void — streamed text delta.
 * @param {function} [opts.onToolUse] (name, args, id) => void — completed tool call.
 * @param {function} [opts.onDone]    () => void — stream finished.
 * @param {function} [opts.onError]   (error) => void — in-band stream error, `agentCause` set.
 * @param {function} [opts.onIncomplete] () => void — stream closed with no terminal event.
 */
export async function streamAgentChat({
  endpoint,
  history,
  context,
  sessionId,
  tools,
  signal,
  onText,
  onToolUse,
  onDone,
  onError,
  onIncomplete,
}) {
  if (signal?.aborted) return;

  const resp = await fetch(`${PRICING_UPDATE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': window.appAuthToken,
    },
    signal,
    body: JSON.stringify({
      sessionId,
      context,
      tools,
      messages: history.map((m) => ({
        role: m.role,
        content: m.content,
      })),
    }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw agentError(err.error?.message || `API error ${resp.status}`, err.error?.cause);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // In-flight tool_use blocks keyed by Anthropic content-block index.
  const toolBlocks = {};

  // Terminal-event bookkeeping — see the "Terminal events" note above.
  let sawMessageStop = false;
  let errorFired = false;
  let doneFired = false;
  const fireDone = () => {
    if (doneFired) return;
    doneFired = true;
    onDone?.();
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep incomplete line in buffer

      for (const line of lines) {
        // SseEmitter wraps each forwarded line in a "data:" prefix
        const stripped = line.startsWith('data:') ? line.slice(5) : line;
        const trimmed = stripped.trim();
        if (!trimmed) continue;

        // Inner Anthropic SSE has its own "event:" line — the data line carries the type
        if (trimmed.startsWith('event:')) continue;

        let dataStr = trimmed;
        if (dataStr.startsWith('data:')) {
          dataStr = dataStr.slice(5).trim();
        }
        if (!dataStr || dataStr === '[DONE]') continue;

        let parsed;
        try {
          parsed = JSON.parse(dataStr);
        } catch (e) {
          continue; // not JSON — comment / keep-alive
        }

        const idx = parsed.index;

        if (parsed.type === 'content_block_start' && parsed.content_block?.type === 'tool_use') {
          toolBlocks[idx] = {
            name: parsed.content_block.name,
            id: parsed.content_block.id,
            input: '',
          };
          continue;
        }

        if (parsed.type === 'content_block_delta') {
          if (parsed.delta?.type === 'text_delta') {
            onText?.(parsed.delta.text);
          } else if (parsed.delta?.type === 'input_json_delta' && toolBlocks[idx]) {
            toolBlocks[idx].input += parsed.delta.partial_json;
          }
          continue;
        }

        if (parsed.type === 'content_block_stop' && toolBlocks[idx]) {
          const block = toolBlocks[idx];
          delete toolBlocks[idx];
          let args = {};
          try { args = JSON.parse(block.input); } catch (e) { /* empty input */ }
          onToolUse?.(block.name, args, block.id);
          continue;
        }

        if (parsed.type === 'message_stop') {
          sawMessageStop = true;
          fireDone();
        }

        if (parsed.type === 'error') {
          // `cause` is absent on an Anthropic-raised error event forwarded
          // verbatim, and on the controller's own catch-all line
          errorFired = true;
          onError?.(agentError(parsed.error?.message || 'Stream error', parsed.error?.cause));
        }
      }
    }
  } catch (err) {
    // Aborts (unmount / Stop) are intentional — swallow silently.
    if (err?.name === 'AbortError' || signal?.aborted) return;
    throw err;
  }

  if (!sawMessageStop && !errorFired) {
    // stream closed with no terminal event — emitter timeout or dropped connection
    onIncomplete?.();
  }

  fireDone();
}
